import React, { useState, useRef } from 'react';

const STANDARD_FIELDS = [
  'FirstName', 'LastName', 'FullName', 'MailingAddress', 'MailingAddress2',
  'MailingCity', 'MailingState', 'MailingZip',
  'PropertyAddress', 'PropertyAddress2', 'PropertyCity', 'PropertyState', 'PropertyZip'
];
const EXTRA_FIELDS = [
  'extra_field1', 'extra_field2', 'extra_field3', 'extra_field4',
  'extra_field5', 'extra_field6', 'extra_field7', 'extra_field8'
];

function FieldMapper({ apiBase }) {
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [extraCount, setExtraCount] = useState(0);  // Number of extra fields to show
  const [error, setError] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  const fileInputRef = useRef(null);

  // --- shared file processing (used by both click-select and drag-drop) ---
  const processFile = (f) => {
    if (!f) return;
    setFile(f);
    setHeaders([]);
    setMapping({});
    setDownloadLink('');
    setError('');
    setExtraCount(0);

    const formData = new FormData();
    formData.append('files', f);
    fetch(`${apiBase}/upload_headers`, {
      method: 'POST',
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          // backend might return HTML on error; fall back to text
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            throw new Error(data.error || 'Failed to read headers');
          } catch {
            throw new Error(text || 'Failed to read headers');
          }
        }
        return res.json();
      })
      .then((arr) => {
        if (arr.length === 1) setHeaders(arr[0].headers);
        else setError('Could not read file headers.');
      })
      .catch((e) => setError(e.message));
  };

  // 1. File input (click-select)
  const handleFile = (e) => {
    const f = e.target.files[0];
    processFile(f);
  };

  // 1b. Drag & drop handlers (minimal; keeps your styling)
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    processFile(f);
  };

  // 2. User picks mapping
  const updateMapping = (field, val) => {
    setMapping((m) => ({ ...m, [field]: val }));
  };

  // 3. Add extra field row (max 8)
  const handleAddExtraField = () => {
    setExtraCount(c => (c < 8 ? c + 1 : c));
  };

  // 4. Submit mapping
  const handleSubmit = async () => {
    setError('');
    setDownloadLink('');
    if (!file) return setError('No file uploaded.');

    // You must map at least one field, but it's OK if some are unmapped
    const mapped = Object.values(mapping).filter(Boolean);
    if (mapped.length === 0) return setError('Please map at least one field.');

    // Only send mapping for standard fields + N extras user added
    const sendingMapping = {};
    STANDARD_FIELDS.forEach(f => { if (mapping[f]) sendingMapping[f] = mapping[f]; });
    for (let i = 0; i < extraCount; ++i) {
      if (mapping[EXTRA_FIELDS[i]]) sendingMapping[EXTRA_FIELDS[i]] = mapping[EXTRA_FIELDS[i]];
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping_json', JSON.stringify(sendingMapping));

    fetch(`${apiBase}/map_headers`, {
      method: 'POST',
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          try {
            const data = JSON.parse(text);
            throw new Error(data.error || 'Failed to process file');
          } catch {
            throw new Error(text || 'Failed to process file');
          }
        }
        return res.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        setDownloadLink(url);
      })
      .catch((e) => setError(e.message));
  };

  return (
    <div id="main-wrapper" style={{ userSelect: 'text', maxWidth: 650, margin: '30px auto', padding: 30 }}>
      <h2>
        <a
          href="/"
          style={{
            color: "#222",
            textDecoration: "none",
            fontWeight: "bold",
            borderBottom: "2px solid #FFD600",
            transition: "border-color 0.2s",
          }}
          onMouseOver={e => e.target.style.borderBottom = "2px solid #111"}
          onMouseOut={e => e.target.style.borderBottom = "2px solid #FFD600"}
        >
          YLHQ - List Manipulator
        </a>
        &nbsp;- Field Mapper
      </h2>

      {/* Upload area with DnD added (styling unchanged) */}
      <div
        className="upload-area"
        onClick={() => fileInputRef.current.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        Click or drag CSV file here to upload
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>

      {file && <div className="uploaded-files" style={{ margin: 14 }}>{file.name}</div>}

      {headers.length > 0 && (
        <>
          <h3 style={{ marginTop: 24, marginBottom: 14 }}>Map CSV headers to YLHQ fields:</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
            <thead>
              <tr><th align="left">YLHQ Field</th><th align="left">CSV Header</th></tr>
            </thead>
            <tbody>
              {STANDARD_FIELDS.map((f, i) => (
                <tr key={f} style={{ background: i % 2 ? '#f8f8f8' : 'white' }}>
                  <td style={{ padding: 6, fontWeight: 700 }}>
                    {f}
                    <span style={{ color: '#b19700', fontWeight: 600 }}> *</span>
                  </td>
                  <td style={{ padding: 6 }}>
                    <select
                      className="select"
                      value={mapping[f] || ''}
                      onChange={(e) => updateMapping(f, e.target.value)}
                    >
                      <option value="">-- None --</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {Array.from({ length: extraCount }).map((_, i) => (
                <tr key={EXTRA_FIELDS[i]} style={{ background: (i + STANDARD_FIELDS.length) % 2 ? '#f8f8f8' : 'white' }}>
                  <td style={{ padding: 6, fontWeight: 400 }}>
                    {EXTRA_FIELDS[i]}
                  </td>
                  <td style={{ padding: 6 }}>
                    <select
                      className="select"
                      value={mapping[EXTRA_FIELDS[i]] || ''}
                      onChange={(e) => updateMapping(EXTRA_FIELDS[i], e.target.value)}
                    >
                      <option value="">-- None --</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {extraCount < 8 && (
            <button className="main-btn" type="button" style={{ marginBottom: 8 }} onClick={handleAddExtraField}>
              Add Extra Field
            </button>
          )}
          <br />
          <button className="main-btn" style={{ marginTop: 10 }} onClick={handleSubmit}>
            Generate CSV with Standard Headers
          </button>
        </>
      )}

      {downloadLink && (
        <div style={{ marginTop: 26 }}>
          <a href={downloadLink} download="ylhq_standardized.csv" className="main-btn">
            Download Standardized CSV
          </a>
        </div>
      )}

      {error && <div className="error-message" style={{ marginTop: 20 }}>{error}</div>}
    </div>
  );
}

export default FieldMapper;
